import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  Globe, 
  Clock, 
  Zap, 
  Search, 
  Filter,
  ExternalLink,
  CheckCircle,
  Play,
  Loader2
} from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';

const API = `${API_BASE}/api`;

// Default thumbnail for external learning modules
const DEFAULT_THUMBNAIL = "https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&auto=format&fit=crop";

const PROVIDERS = [
  { value: 'all', label: 'All Providers' },
  { value: 'scorm_cloud', label: 'SCORM Cloud' },
  { value: 'intuition', label: 'Intuition' },
  { value: 'custom', label: 'Custom' },
];

export default function ExternalLearningPage() {
  const { token, isAuthenticated } = useAuth();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [provider, setProvider] = useState('all');
  const [userProgress, setUserProgress] = useState({});

  useEffect(() => {
    fetchModules();
    if (isAuthenticated) {
      fetchUserProgress();
    }
  }, [isAuthenticated]);

  const fetchModules = async () => {
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/external-learning/modules`, { headers });
      setModules(res.data || []);
    } catch (error) {
      console.error('Error fetching external learning modules:', error);
      toast.error('Failed to load external learning modules');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserProgress = async () => {
    try {
      // Fetch progress for all modules the user has started
      const res = await axios.get(`${API}/external-learning/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const progressMap = {};
      for (const module of res.data || []) {
        try {
          const progressRes = await axios.get(`${API}/external-learning/progress/${module.module_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          progressMap[module.module_id] = progressRes.data;
        } catch (e) {
          // Progress not found is fine
        }
      }
      setUserProgress(progressMap);
    } catch (error) {
      console.error('Error fetching user progress:', error);
    }
  };

  const filteredModules = modules.filter(module => {
    const matchesSearch = !search || 
      module.title.toLowerCase().includes(search.toLowerCase()) ||
      module.description?.toLowerCase().includes(search.toLowerCase());
    const matchesProvider = provider === 'all' || module.provider === provider;
    return matchesSearch && matchesProvider;
  });

  const getProgressStatus = (moduleId) => {
    const progress = userProgress[moduleId];
    if (!progress || progress.status === 'not_started') return null;
    return progress.status;
  };

  const formatDuration = (minutes) => {
    if (!minutes) return '30 mins';
    if (minutes < 60) return `${minutes} mins`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="external-learning-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <Globe className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold text-white">
                External Learning
              </h1>
              <p className="text-zinc-400 mt-1">
                Third-party courses and content from our partner providers
              </p>
            </div>
          </div>
          
          {/* Info Banner */}
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 border border-blue-500/30 rounded-xl p-4 mt-4">
            <div className="flex items-start gap-3">
              <ExternalLink className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm text-blue-300 font-medium">Powered by SCORM Cloud</p>
                <p className="text-xs text-blue-200/70 mt-1">
                  These courses are delivered through secure iframe embedding. Your progress is tracked automatically.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search external courses..."
              className="pl-12 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 h-12"
              data-testid="external-learning-search"
            />
          </div>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="w-full sm:w-[200px] bg-navigate-surface border-zinc-800 h-12" data-testid="provider-filter">
              <Filter className="h-4 w-4 mr-2 text-zinc-400" />
              <SelectValue placeholder="Provider" />
            </SelectTrigger>
            <SelectContent className="bg-navigate-surface border-zinc-800">
              {PROVIDERS.map(p => (
                <SelectItem key={p.value} value={p.value} className="hover:bg-zinc-800">
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results Count */}
        <div className="mb-4 flex items-center justify-between">
          <p className="text-sm text-zinc-400">
            {loading ? 'Loading...' : `${filteredModules.length} course${filteredModules.length !== 1 ? 's' : ''} available`}
          </p>
        </div>

        {/* Modules Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden animate-pulse">
                <div className="aspect-video bg-zinc-800" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-zinc-800 rounded w-1/3" />
                  <div className="h-5 bg-zinc-800 rounded" />
                  <div className="h-4 bg-zinc-800 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredModules.length === 0 ? (
          <div className="text-center py-16">
            <Globe className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-xl font-medium text-white mb-2">No external courses found</h3>
            <p className="text-zinc-400 mb-6">
              {search || provider !== 'all' 
                ? 'Try adjusting your filters'
                : 'Check back soon for new external content'}
            </p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredModules.map((module) => {
              const status = getProgressStatus(module.module_id);
              const isCompleted = status === 'completed' || status === 'passed';
              const isInProgress = status === 'in_progress';
              
              return (
                <Link
                  key={module.module_id}
                  to={`/external-learning/${module.module_id}`}
                  className="group relative bg-navigate-surface border border-zinc-800 rounded-xl overflow-hidden hover:border-blue-500/50 transition-all duration-300 hover:-translate-y-1 block"
                  data-testid={`external-module-card-${module.module_id}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video w-full overflow-hidden bg-zinc-800 relative">
                    <img 
                      src={module.thumbnail_url || DEFAULT_THUMBNAIL}
                      alt={module.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={(e) => {
                        e.target.src = DEFAULT_THUMBNAIL;
                        e.target.onerror = null;
                      }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    
                    {/* Duration Badge */}
                    <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-white flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDuration(module.estimated_duration_minutes)}
                    </div>
                    
                    {/* External Badge */}
                    <div className="absolute top-3 left-3 bg-blue-500/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-white flex items-center gap-1">
                      <Globe className="h-3 w-3" />
                      External
                    </div>
                    
                    {/* Status Overlay */}
                    {isCompleted && (
                      <div className="absolute bottom-3 left-3 bg-green-500/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-white flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        Completed
                      </div>
                    )}
                    {isInProgress && (
                      <div className="absolute bottom-3 left-3 bg-yellow-500/90 backdrop-blur-sm px-2 py-1 rounded text-xs font-medium text-black flex items-center gap-1">
                        <Play className="h-3 w-3" />
                        In Progress
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    {/* Provider & Category */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-300 rounded-full capitalize">
                        {module.provider?.replace('_', ' ') || 'SCORM Cloud'}
                      </span>
                      {module.category && (
                        <span className="text-xs text-zinc-500">{module.category}</span>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-heading font-semibold text-white text-lg mb-2 group-hover:text-blue-400 transition-colors line-clamp-2">
                      {module.title}
                    </h3>

                    {/* Description */}
                    {module.description && (
                      <p className="text-sm text-zinc-400 mb-4 line-clamp-2">
                        {module.description}
                      </p>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-4 border-t border-zinc-800">
                      <div className="flex items-center gap-1 text-navigate-gold">
                        <Zap className="h-4 w-4" />
                        <span className="text-sm font-medium">{module.xp_reward || 100} XP</span>
                      </div>
                      
                      <span className="text-sm text-blue-400 font-medium group-hover:translate-x-1 transition-transform">
                        {isCompleted ? 'Review' : isInProgress ? 'Continue' : 'Start'} →
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
